/**
 * @description
 *	Color manipulation functions over the sRGB color space.
 */
export default class Color {
	/**
	 * @description
	 *	Given an opaque sRGB color written as "#RRGGBB", computes its relative
	 *	luminance, normalized in the interval [0, 1] where 0 (zero) is the
	 *	darkest black and 1 (one) is the lightest white.
	 *
	 * @see https://www.w3.org/TR/WCAG22/#dfn-relative-luminance
	 */
	private static relativeLuminance(hexadecimalColor: string): number {
		const [red, green, blue] = [1, 3, 5]
			.map(index => Number.parseInt(hexadecimalColor.substring(index, index + 2), 16) / 255)
			.map(channel => (channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
		return 0.2126 * red + 0.7152 * green + 0.0722 * blue
	}

	/**
	 * @description
	 *	Given 2 (two) opaque sRGB colors written as "#RRGGBB", computes the
	 *	contrast ratio between them, normalized in the interval [1, 21] where
	 *	1 (one) means both colors are identical.
	 *
	 * @see https://www.w3.org/TR/WCAG22/#dfn-contrast-ratio
	 */
	public static contrastRatio(hexadecimalColor: string, otherHexadecimalColor: string): number {
		const luminance = Color.relativeLuminance(hexadecimalColor)
		const otherLuminance = Color.relativeLuminance(otherHexadecimalColor)
		return (Math.max(luminance, otherLuminance) + 0.05) / (Math.min(luminance, otherLuminance) + 0.05)
	}
}
